import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import { toast, type BnBadgeVariant, type BnColumn, type BnRow } from "../components/index.js";
import { navigate } from "../nav.js";
import { detailedError, humanError } from "../api/errors.js";
import { getRepository, type RepositoryDetail } from "../api/repositories.js";
import { listTeams, roleAtLeast, type Role } from "../api/teams.js";
import { listCalendars, type Calendar } from "../api/calendars.js";
import {
  getDeployment,
  updateDeployment,
  deleteDeployment,
  enable,
  stop,
  start,
  mintTeamToken,
  rotateLinkKey,
  rotateEditionKey,
  sendNow,
  listEditions,
  getWebhook,
  putWebhook,
  deleteWebhook,
  rotateWebhookSecret,
  listWebhookDeliveries,
  listMails,
  parseAudience,
  PERIOD_RULES,
  type Channel,
  type Deployment,
  type EditionSummary,
  type PeriodRule,
  type ReportMail,
  type Schedule,
  type UpdateDeploymentRequest,
  type Webhook,
  type WebhookDelivery,
} from "../api/deployments.js";

const CHANNELS: ReadonlyArray<{ value: Channel; label: string; hint: string }> = [
  { value: "live", label: "Live URL", hint: "Serves the report at its permanent URL." },
  { value: "mail", label: "Mail", hint: "Sends the audience a signed link to each edition." },
  { value: "webhook", label: "Webhook", hint: "Posts a signed event for each edition." },
];

function formatTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function statusVariant(status: string): BnBadgeVariant {
  switch (status) {
    case "live":
    case "delivered":
    case "done":
    case "sent":
    case "succeeded":
      return "success";
    case "stopped":
    case "late":
    case "blocked":
    case "queued":
      return "warning";
    case "failed":
      return "error";
    default:
      return "neutral";
  }
}

/** describeSchedule spells a schedule for people. */
function describeSchedule(s?: Schedule): string {
  if (!s) return "None (on demand only)";
  if (s.kind === "workday") return `Workday ${s.day} of each month at ${s.at}`;
  return `Cron ${s.cron}`;
}

/**
 * page-deployment (/repositories/:id/deployments/:deploymentId) is one
 * deployment: its settings, its schedule and open window, its channels, the
 * last edition its schedule asked for, its editions, and the webhook
 * attempts and mails it sent. Everyone on the team reads it. A maintainer
 * enables a draft, stops and starts it, sends an edition now, changes its
 * settings, channels and audience, sets its webhook and rotates its keys,
 * as the server allows only them.
 */
@customElement("page-deployment")
export class PageDeployment extends LitElement {
  @property() repositoryId = "";
  @property() deploymentId = "";

  @state() private repo: RepositoryDetail | null = null;
  @state() private teamRole: Role = "viewer";
  @state() private dep: Deployment | null = null;
  @state() private calendars: Calendar[] = [];
  @state() private loading = true;
  @state() private error = "";
  @state() private busy = "";

  @state() private editions: EditionSummary[] = [];
  @state() private nextBefore: number | undefined = undefined;
  /** undefined until read; null when the deployment has none. */
  @state() private webhook: Webhook | null | undefined = undefined;
  @state() private deliveries: WebhookDelivery[] = [];
  @state() private mails: ReportMail[] = [];

  @state() private modal = "";
  @state() private formError = "";
  @state() private fCommit = "";
  @state() private fRule: PeriodRule = "previous_month";
  @state() private fKind: "" | "workday" | "cron" = "";
  @state() private fDay = "3";
  @state() private fAt = "09:00";
  @state() private fCron = "";
  @state() private fZone = "";
  @state() private fCalendar = "";
  @state() private fWindow = "3";
  @state() private fChannels: Channel[] = [];
  @state() private fAudience = "";
  @state() private fTtl = "14";
  @state() private fURL = "";
  @state() private fPeriod = "";
  /** A webhook signing secret the server answered once. */
  @state() private secret = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-flex;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      margin-bottom: var(--bino-space-md);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-lg);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0 var(--bino-space-sm) 0 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .spacer {
      flex: 1;
    }
    .sections {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .meta {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
      gap: var(--bino-space-md);
    }
    .meta .k {
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
      margin-bottom: var(--bino-space-xs);
    }
    .meta .v {
      font-size: var(--bino-font-size-md);
      word-break: break-all;
    }
    .mono {
      font-family: var(--bino-font-mono);
    }
    .hint {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0;
    }
    .err {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-error);
      margin: 0 0 var(--bino-space-sm);
    }
    .actions-row {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-top: var(--bino-space-md);
    }
    .row {
      display: flex;
      align-items: flex-end;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .row > bn-field {
      flex: 1;
      min-width: 8rem;
    }
    .channel {
      padding: var(--bino-space-sm) 0;
      border-bottom: 1px solid var(--bino-border-light);
    }
    .channel:last-child {
      border-bottom: none;
    }
    .channel-head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      font-weight: 600;
    }
    .link-btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-size: var(--bino-font-size-base);
      color: var(--bino-primary);
      text-decoration: underline;
    }
    .warn {
      margin-bottom: var(--bino-space-lg);
      padding: var(--bino-space-md);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      color: var(--bino-warning-text);
    }
    .code {
      display: block;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      padding: var(--bino-space-sm);
      background: var(--bino-surface-subtle);
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      word-break: break-all;
    }
    select,
    textarea {
      width: 100%;
      box-sizing: border-box;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
    }
    textarea {
      min-height: 6rem;
      font-family: var(--bino-font-mono);
    }
    select:focus,
    textarea:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
  `;

  override updated(changed: PropertyValues) {
    if ((changed.has("repositoryId") || changed.has("deploymentId")) && this.deploymentId) {
      void this.load();
    }
  }

  private get base(): string {
    return `/repositories/${encodeURIComponent(this.repositoryId)}/deployments`;
  }

  private get maintainer(): boolean {
    return roleAtLeast(this.teamRole, "maintainer");
  }

  private async load() {
    const key = `${this.repositoryId}/${this.deploymentId}`;
    const stale = () => !this.isConnected || key !== `${this.repositoryId}/${this.deploymentId}`;
    this.loading = true;
    this.error = "";
    try {
      const [repo, dep, teams] = await Promise.all([
        getRepository(this.repositoryId),
        getDeployment(this.repositoryId, this.deploymentId),
        listTeams().catch(() => []),
      ]);
      if (stale()) return;
      this.repo = repo;
      this.dep = dep;
      this.teamRole = teams.find((t) => t.id === repo.teamId)?.role ?? "viewer";
    } catch (e) {
      if (stale()) return;
      this.error = humanError(e);
      this.loading = false;
      return;
    }
    this.loading = false;
    void this.loadSide();
  }

  /** loadSide reads what the role of the caller may see beside the deployment. */
  private async loadSide() {
    const id = this.repositoryId;
    const dep = this.deploymentId;
    try {
      const page = await listEditions(id, dep);
      this.editions = page.items;
      this.nextBefore = page.nextBefore;
      this.calendars = (await listCalendars(this.repo?.teamId ?? "")) ?? [];
      if (roleAtLeast(this.teamRole, "member"))
        this.deliveries = await listWebhookDeliveries(id, dep);
      if (this.maintainer) {
        this.webhook = await getWebhook(id, dep);
        this.mails = await listMails(id, dep);
      }
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private async moreEditions() {
    try {
      const page = await listEditions(this.repositoryId, this.deploymentId, this.nextBefore);
      this.editions = [...this.editions, ...page.items];
      this.nextBefore = page.nextBefore;
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  /** act runs one change of the deployment and shows its answer. */
  private async act(name: string, f: () => Promise<Deployment | void>, done: string) {
    this.busy = name;
    try {
      const d = await f();
      if (d) this.dep = { ...this.dep, ...d };
      toast(done, "success");
      return true;
    } catch (e) {
      toast(detailedError(e), "error");
      return false;
    } finally {
      this.busy = "";
    }
  }

  private async openLive() {
    const d = this.dep;
    if (!d?.url) return;
    try {
      const url =
        d.visibility === "team"
          ? `${d.url}?t=${encodeURIComponent((await mintTeamToken(this.repositoryId, d.id)).token)}`
          : d.url;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  // ---- Forms ----

  private openSettings() {
    const d = this.dep;
    if (!d) return;
    this.fCommit = d.commit ?? "";
    this.fRule = d.periodRule ?? "previous_month";
    this.fKind = d.schedule?.kind ?? "";
    this.fDay = String(d.schedule?.day ?? 3);
    this.fAt = d.schedule?.at ?? "09:00";
    this.fCron = d.schedule?.cron ?? "";
    this.fZone = d.timezone || "UTC";
    this.fCalendar = d.calendarId ?? "";
    this.fWindow = String(d.windowWorkdays ?? 3);
    this.formError = "";
    this.modal = "settings";
  }

  private async saveSettings() {
    const d = this.dep;
    if (!d) return;
    const schedule: Schedule | null =
      this.fKind === "workday"
        ? { kind: "workday", day: Number(this.fDay), at: this.fAt.trim() }
        : this.fKind === "cron"
          ? { kind: "cron", cron: this.fCron.trim() }
          : null;
    const req: UpdateDeploymentRequest = {
      periodRule: this.fRule,
      schedule,
      timezone: this.fZone.trim(),
      calendarId: this.fCalendar,
      windowWorkdays: Number(this.fWindow),
    };
    // A legacy deployment takes its settings only with a commit, which
    // deploys it there.
    if (this.fCommit.trim() !== (d.commit ?? "") || d.legacy) req.commit = this.fCommit.trim();
    await this.submit(() => updateDeployment(this.repositoryId, d.id, req), "Deployment saved");
  }

  private openChannels(kind: "enable" | "channels") {
    const d = this.dep;
    if (!d) return;
    this.fChannels =
      kind === "channels"
        ? [...(d.channels ?? [])]
        : (["live", "mail", "webhook"] as Channel[]).filter(
            (c) =>
              c === "live" ||
              (c === "mail" && (d.audienceCount ?? 0) > 0) ||
              (c === "webhook" && !!this.webhook),
          );
    this.formError = "";
    this.modal = kind;
  }

  private toggleChannel(c: Channel, on: boolean) {
    const set = new Set(this.fChannels);
    if (on) set.add(c);
    else set.delete(c);
    this.fChannels = CHANNELS.map((x) => x.value).filter((x) => set.has(x));
  }

  private async saveChannels() {
    const d = this.dep;
    if (!d) return;
    if (this.modal === "enable") {
      await this.submit(
        () => enable(this.repositoryId, d.id, this.fChannels),
        "Deployment enabled",
      );
      return;
    }
    await this.submit(
      () => updateDeployment(this.repositoryId, d.id, { channels: this.fChannels }),
      "Channels saved",
    );
  }

  private openAudience() {
    const d = this.dep;
    if (!d) return;
    this.fAudience = (d.audience ?? []).join("\n");
    this.fTtl = String(d.mailLinkTtlDays ?? 14);
    this.formError = "";
    this.modal = "audience";
  }

  private async saveAudience() {
    const d = this.dep;
    if (!d) return;
    await this.submit(
      () =>
        updateDeployment(this.repositoryId, d.id, {
          audience: parseAudience(this.fAudience),
          mailLinkTtlDays: Number(this.fTtl),
        }),
      "Audience saved",
    );
  }

  /** submit runs a form's change; an error stays in the form. */
  private async submit(f: () => Promise<Deployment>, done: string) {
    this.busy = "form";
    this.formError = "";
    try {
      this.dep = { ...this.dep, ...(await f()) };
      this.modal = "";
      toast(done, "success");
    } catch (e) {
      this.formError = detailedError(e);
    } finally {
      this.busy = "";
    }
  }

  private async saveWebhook() {
    this.busy = "form";
    this.formError = "";
    try {
      const res = await putWebhook(this.repositoryId, this.deploymentId, this.fURL.trim());
      this.webhook = res.webhook;
      this.modal = "";
      if (res.secret) {
        this.secret = res.secret;
        this.modal = "secret";
      } else {
        toast("Webhook saved", "success");
      }
    } catch (e) {
      this.formError = detailedError(e);
    } finally {
      this.busy = "";
    }
  }

  private async rotateSecret() {
    this.modal = "";
    try {
      const res = await rotateWebhookSecret(this.repositoryId, this.deploymentId);
      this.secret = res.secret;
      this.modal = "secret";
      this.webhook = await getWebhook(this.repositoryId, this.deploymentId);
    } catch (e) {
      toast(detailedError(e), "error");
    }
  }

  private async removeWebhook() {
    this.modal = "";
    if (
      await this.act(
        "webhook",
        () => deleteWebhook(this.repositoryId, this.deploymentId),
        "Webhook removed",
      )
    ) {
      this.webhook = null;
    }
  }

  private async send() {
    const d = this.dep;
    if (!d) return;
    this.busy = "form";
    this.formError = "";
    try {
      const res = await sendNow(this.repositoryId, d.id, this.fPeriod.trim() || undefined);
      this.modal = "";
      toast(`Edition for ${res.period} queued`, "success");
      void this.refresh();
    } catch (e) {
      this.formError = detailedError(e);
    } finally {
      this.busy = "";
    }
  }

  private async confirm(kind: string) {
    const d = this.dep;
    if (!d) return;
    this.modal = "";
    switch (kind) {
      case "delete":
        if (
          await this.act(
            "delete",
            () => deleteDeployment(this.repositoryId, d.id),
            "Deployment deleted",
          )
        ) {
          navigate(this.base);
        }
        break;
      case "link-key":
        await this.act(
          "keys",
          () => rotateLinkKey(this.repositoryId, d.id),
          "Viewer-link key rotated",
        );
        break;
      case "edition-key":
        await this.act(
          "keys",
          () => rotateEditionKey(this.repositoryId, d.id),
          "Edition key rotated",
        );
        break;
      case "rotate-secret":
        await this.rotateSecret();
        break;
      case "remove-webhook":
        await this.removeWebhook();
        break;
    }
  }

  private async refresh() {
    try {
      this.dep = await getDeployment(this.repositoryId, this.deploymentId);
    } catch (e) {
      toast(humanError(e), "error");
    }
    void this.loadSide();
  }

  private async copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast("Copied to clipboard", "success");
    } catch {
      toast("Couldn't copy. Select the text and copy it yourself.", "error");
    }
  }

  // ---- Render ----

  override render() {
    if (this.loading && !this.dep) {
      return html`<bn-spinner block label="Loading deployment…"></bn-spinner>`;
    }
    if (this.error || !this.dep || !this.repo) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load the deployment"
        message=${this.error || "Deployment not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate(this.base)}
          >Back to deployments</bn-button
        >
      </bn-empty-state>`;
    }
    const d = this.dep;
    return html`
      <a
        class="back"
        href=${this.base}
        @click=${(e: Event) => {
          e.preventDefault();
          navigate(this.base);
        }}
        >← ${this.repo.name} deployments</a
      >
      <div class="head">
        <h1>${d.artefact}</h1>
        <bn-badge .variant=${statusVariant(d.status)} ?dot=${d.status === "live"}
          >${d.status}</bn-badge
        >
        ${d.legacy ? html`<bn-badge variant="info">legacy</bn-badge>` : nothing}
        <span class="spacer"></span>
        <bn-button variant="ghost" size="sm" @click=${() => this.refresh()}>Refresh</bn-button>
        ${this.renderActions(d)}
      </div>
      ${d.mailUnavailable
        ? html`<div class="warn">
            The mail channel is on, but this server has no report mail configured. Mails wait until
            your admin sets it up.
          </div>`
        : nothing}
      <div class="sections">
        ${this.renderSettings(d)} ${this.renderSchedule(d)} ${this.renderLastRun(d)}
        ${this.renderChannels(d)} ${this.maintainer ? this.renderKeys() : nothing}
        ${this.renderEditions()} ${this.renderDeliveries()} ${this.renderMails()}
      </div>
      ${this.renderModals(d)}
    `;
  }

  private renderActions(d: Deployment) {
    if (!this.maintainer) return nothing;
    const busy = this.busy !== "";
    return html`
      ${d.status === "draft"
        ? html`<bn-button
            size="sm"
            variant="primary"
            ?disabled=${busy}
            @click=${() => this.openChannels("enable")}
            >Enable</bn-button
          >`
        : nothing}
      ${d.status === "live"
        ? html`<bn-button
              size="sm"
              variant="primary"
              ?disabled=${busy}
              @click=${() => {
                this.fPeriod = "";
                this.formError = "";
                this.modal = "send";
              }}
              >Send now</bn-button
            >
            <bn-button
              size="sm"
              variant="secondary"
              ?loading=${this.busy === "stop"}
              @click=${() =>
                this.act("stop", () => stop(this.repositoryId, d.id), "Deployment stopped")}
              >Stop</bn-button
            >`
        : nothing}
      ${d.status === "stopped"
        ? html`<bn-button
            size="sm"
            variant="secondary"
            ?loading=${this.busy === "start"}
            @click=${() =>
              this.act("start", () => start(this.repositoryId, d.id), "Deployment started")}
            >Start</bn-button
          >`
        : nothing}
      <bn-button
        size="sm"
        variant="danger"
        ?disabled=${busy}
        @click=${() => (this.modal = "delete")}
        >Delete</bn-button
      >
    `;
  }

  private renderSettings(d: Deployment) {
    const calendar = d.calendarId
      ? (this.calendars.find((c) => c.id === d.calendarId)?.name ?? d.calendarId)
      : "Monday to Friday";
    return html`<bn-card heading="Settings">
      ${this.maintainer
        ? html`<bn-button
            slot="actions"
            size="sm"
            variant="secondary"
            @click=${() => this.openSettings()}
            >Edit</bn-button
          >`
        : nothing}
      <div class="meta">
        <div>
          <div class="k">Commit</div>
          <div class="v mono">${d.commit || (d.legacy ? "a workspace snapshot" : "—")}</div>
        </div>
        <div>
          <div class="k">Period rule</div>
          <div class="v">${PERIOD_RULES.find((r) => r.value === d.periodRule)?.label ?? "—"}</div>
        </div>
        <div>
          <div class="k">Time zone</div>
          <div class="v mono">${d.timezone || "—"}</div>
        </div>
        <div>
          <div class="k">Calendar</div>
          <div class="v">${calendar}</div>
        </div>
        <div>
          <div class="k">Window</div>
          <div class="v">${d.windowWorkdays ?? 3} workdays</div>
        </div>
        <div>
          <div class="k">Visibility</div>
          <div class="v">${d.visibility}</div>
        </div>
      </div>
      ${d.legacy
        ? html`<p class="hint" style="margin-top:var(--bino-space-md)">
            This deployment still serves the snapshot of the workspace it was published from. Deploy
            it at a commit to give it a schedule, mail and a webhook.
          </p>`
        : nothing}
    </bn-card>`;
  }

  private renderSchedule(d: Deployment) {
    const w = d.openWindow;
    return html`<bn-card heading="Schedule">
      <div class="meta">
        <div>
          <div class="k">When</div>
          <div class="v">${describeSchedule(d.schedule)}</div>
        </div>
        <div>
          <div class="k">Next edition</div>
          <div class="v">${formatTime(d.nextDueAt)}</div>
        </div>
      </div>
      ${w
        ? html`<p class="hint" style="margin-top:var(--bino-space-md)">
            The edition for <strong>${w.period}</strong> waits for its data until
            ${formatTime(w.closesAt)}.
            ${w.missingMonths.length
              ? html`Months without a seal: <span class="mono">${w.missingMonths.join(", ")}</span>.
                  If the window closes first, nothing is built and the maintainers get a notice.`
              : "Every month is sealed. The build starts within a minute."}
          </p>`
        : nothing}
    </bn-card>`;
  }

  private renderLastRun(d: Deployment) {
    const r = d.lastRun;
    if (!r) return nothing;
    return html`<bn-card heading="Last run">
      <div class="meta">
        <div>
          <div class="k">Period</div>
          <div class="v mono">${r.period || "—"}</div>
        </div>
        <div>
          <div class="k">Due</div>
          <div class="v">${formatTime(r.dueAt)}</div>
        </div>
        <div>
          <div class="k">Status</div>
          <div class="v">
            <bn-badge .variant=${statusVariant(r.status)}>${r.status}</bn-badge>
            ${r.error ? html`<span class="mono">${r.error}</span>` : nothing}
          </div>
        </div>
        <div>
          <div class="k">Build</div>
          <div class="v">
            ${r.buildId
              ? html`<button class="link-btn" @click=${() => navigate(`/builds/${r.buildId}`)}>
                  ${r.buildStatus ?? r.buildId}
                </button>`
              : "—"}
          </div>
        </div>
        <div>
          <div class="k">Edition</div>
          <div class="v">
            ${r.editionId
              ? html`<button
                  class="link-btn"
                  @click=${() =>
                    navigate(`${this.base}/${encodeURIComponent(d.id)}/editions/${r.editionId}`)}
                >
                  ${r.editionStatus ?? r.editionId}
                </button>`
              : "—"}
          </div>
        </div>
      </div>
    </bn-card>`;
  }

  private renderChannels(d: Deployment) {
    const on = (c: Channel) => d.channels?.includes(c) ?? false;
    const badge = (c: Channel) =>
      html`<bn-badge .variant=${on(c) ? "success" : "neutral"}>${on(c) ? "on" : "off"}</bn-badge>`;
    const w = this.webhook;
    return html`<bn-card heading="Channels">
      ${this.maintainer && d.status !== "draft"
        ? html`<bn-button
            slot="actions"
            size="sm"
            variant="secondary"
            @click=${() => this.openChannels("channels")}
            >Change channels</bn-button
          >`
        : nothing}
      <div class="channel">
        <div class="channel-head">Live URL ${badge("live")}</div>
        ${d.url
          ? html`<div class="actions-row">
              <span class="code">${d.url}</span>
              ${on("live") && d.status === "live" && d.visibility !== "token"
                ? html`<bn-button size="sm" variant="secondary" @click=${() => this.openLive()}
                    >Open</bn-button
                  >`
                : nothing}
            </div>`
          : html`<p class="hint">Live hosting is not configured on this server.</p>`}
      </div>
      <div class="channel">
        <div class="channel-head">Mail ${badge("mail")}</div>
        <p class="hint">
          ${d.audienceCount ?? 0} recipients. Mail links work for ${d.mailLinkTtlDays ?? 14} days.
        </p>
        ${d.audience?.length ? html`<p class="hint mono">${d.audience.join(", ")}</p>` : nothing}
        ${this.maintainer
          ? html`<div class="actions-row">
              <bn-button size="sm" variant="secondary" @click=${() => this.openAudience()}
                >Edit audience</bn-button
              >
            </div>`
          : nothing}
      </div>
      <div class="channel">
        <div class="channel-head">Webhook ${badge("webhook")}</div>
        ${!this.maintainer
          ? html`<p class="hint">A maintainer sets the webhook.</p>`
          : w === undefined
            ? nothing
            : w === null
              ? html`<p class="hint">No webhook yet.</p>`
              : html`<p class="hint mono">${w.url || w.host}</p>
                  <p class="hint">
                    ${w.enabled
                      ? html`${w.failures} failed attempts in a row.`
                      : html`<bn-badge variant="error">switched off</bn-badge> after ${w.failures}
                          failed attempts. Save the URL again to switch it on.`}
                    ${w.previousSecretUntil
                      ? html`The previous secret also signs until
                        ${formatTime(w.previousSecretUntil)}.`
                      : nothing}
                  </p>`}
        ${this.maintainer
          ? html`<div class="actions-row">
              <bn-button
                size="sm"
                variant="secondary"
                @click=${() => {
                  this.fURL = this.webhook?.url ?? "";
                  this.formError = "";
                  this.modal = "webhook";
                }}
                >${w ? "Change URL" : "Set URL"}</bn-button
              >
              ${w
                ? html`<bn-button
                      size="sm"
                      variant="secondary"
                      @click=${() => (this.modal = "rotate-secret")}
                      >Rotate secret</bn-button
                    >
                    <bn-button
                      size="sm"
                      variant="danger"
                      @click=${() => (this.modal = "remove-webhook")}
                      >Remove</bn-button
                    >`
                : nothing}
            </div>`
          : nothing}
      </div>
    </bn-card>`;
  }

  private renderKeys() {
    return html`<bn-card heading="Keys">
      <p class="hint">
        Rotating a key invalidates every link signed with it at once: the viewer links of the live
        URL, or the links in every report mail sent so far.
      </p>
      <div class="actions-row">
        <bn-button
          size="sm"
          variant="secondary"
          ?loading=${this.busy === "keys"}
          @click=${() => (this.modal = "link-key")}
          >Rotate viewer-link key</bn-button
        >
        <bn-button
          size="sm"
          variant="secondary"
          ?loading=${this.busy === "keys"}
          @click=${() => (this.modal = "edition-key")}
          >Rotate edition key</bn-button
        >
      </div>
    </bn-card>`;
  }

  private renderEditions() {
    const open = (ed: EditionSummary) =>
      navigate(
        `${this.base}/${encodeURIComponent(this.deploymentId)}/editions/${encodeURIComponent(ed.id)}`,
      );
    return html`<bn-card heading="Editions" flush>
      <bn-table
        .columns=${[
          { key: "seq", label: "#", align: "right", mono: true, width: "1px" },
          { key: "period", label: "Period", mono: true },
          { key: "status", label: "Status" },
          { key: "trigger", label: "Trigger" },
          { key: "size", label: "Size", align: "right", mono: true },
          { key: "created", label: "Made" },
        ] as BnColumn[]}
        .rows=${this.editions.map(
          (ed): BnRow => ({
            seq: html`<button class="link-btn" @click=${() => open(ed)}>${ed.seq}</button>`,
            period: ed.period,
            status: html`<bn-badge .variant=${statusVariant(ed.status)}>${ed.status}</bn-badge>
              ${ed.correctionOf ? html`<bn-badge variant="info">correction</bn-badge>` : nothing}`,
            trigger: ed.trigger ?? "—",
            size: formatBytes(ed.bytes),
            created: formatTime(ed.created),
          }),
        )}
        empty="No editions yet."
      ></bn-table>
      ${this.nextBefore
        ? html`<div style="padding:var(--bino-space-md)">
            <bn-button size="sm" variant="ghost" @click=${() => this.moreEditions()}
              >Load more</bn-button
            >
          </div>`
        : nothing}
    </bn-card>`;
  }

  private renderDeliveries() {
    if (!roleAtLeast(this.teamRole, "member") || this.deliveries.length === 0) return nothing;
    return html`<bn-card heading="Webhook attempts" flush>
      <bn-table
        .columns=${[
          { key: "event", label: "Event" },
          { key: "period", label: "Period", mono: true },
          { key: "attempt", label: "Attempt", align: "right", mono: true, width: "1px" },
          { key: "status", label: "Status" },
          { key: "http", label: "HTTP", mono: true },
          { key: "at", label: "Due" },
        ] as BnColumn[]}
        .rows=${this.deliveries.map(
          (w): BnRow => ({
            event: html`<span title=${w.eventId}>${w.event}</span>`,
            period: w.period || "—",
            attempt: String(w.attempt),
            status: html`<bn-badge .variant=${statusVariant(w.status)}>${w.status}</bn-badge>
              ${w.reason ? html`<span class="mono">${w.reason}</span>` : nothing}`,
            http: w.httpStatus ? String(w.httpStatus) : "—",
            at: formatTime(w.dueAt),
          }),
        )}
      ></bn-table>
    </bn-card>`;
  }

  private renderMails() {
    if (!this.maintainer || this.mails.length === 0) return nothing;
    return html`<bn-card heading="Mails" flush>
      <bn-table
        .columns=${[
          { key: "kind", label: "Kind" },
          { key: "to", label: "To", mono: true },
          { key: "status", label: "Status" },
          { key: "at", label: "Sent" },
        ] as BnColumn[]}
        .rows=${this.mails.map(
          (m): BnRow => ({
            kind: m.kind,
            to: m.recipient,
            status: html`<bn-badge .variant=${statusVariant(m.status)}>${m.status}</bn-badge>
              ${m.reason ? html`<span class="mono">${m.reason}</span>` : nothing}`,
            at: formatTime(m.sentAt ?? m.nextAttemptAt),
          }),
        )}
      ></bn-table>
    </bn-card>`;
  }

  private renderModals(d: Deployment) {
    const close = () => (this.modal = "");
    const loading = this.busy === "form";
    const err = this.formError ? html`<p class="err">${this.formError}</p>` : nothing;
    return html`
      <bn-modal
        ?open=${this.modal === "settings"}
        width=${600}
        heading="Edit deployment"
        @close=${close}
      >
        ${err}
        <bn-field
          label="Commit"
          hint=${d.legacy
            ? "Required: the deployment moves from its snapshot to this commit."
            : "A full commit ID. Enter another one to deploy it."}
        >
          <bn-input
            mono
            .value=${this.fCommit}
            @input=${(e: CustomEvent<{ value: string }>) => (this.fCommit = e.detail.value)}
          ></bn-input>
        </bn-field>
        <div class="row">
          <bn-field label="Period rule">
            <select
              .value=${this.fRule}
              @change=${(e: Event) =>
                (this.fRule = (e.target as HTMLSelectElement).value as PeriodRule)}
            >
              ${PERIOD_RULES.map(
                (r) =>
                  html`<option value=${r.value} ?selected=${r.value === this.fRule}>
                    ${r.label}
                  </option>`,
              )}
            </select>
          </bn-field>
          <bn-field label="Schedule">
            <select
              .value=${this.fKind}
              @change=${(e: Event) =>
                (this.fKind = (e.target as HTMLSelectElement).value as "" | "workday" | "cron")}
            >
              <option value="workday" ?selected=${this.fKind === "workday"}>
                Workday of the month
              </option>
              <option value="cron" ?selected=${this.fKind === "cron"}>Cron expression</option>
              <option value="" ?selected=${this.fKind === ""}>None (on demand only)</option>
            </select>
          </bn-field>
        </div>
        ${this.fKind === "workday"
          ? html`<div class="row">
              <bn-field label="Workday">
                <bn-input
                  type="number"
                  .value=${this.fDay}
                  @input=${(e: CustomEvent<{ value: string }>) => (this.fDay = e.detail.value)}
                ></bn-input>
              </bn-field>
              <bn-field label="At" hint="HH:MM">
                <bn-input
                  mono
                  .value=${this.fAt}
                  @input=${(e: CustomEvent<{ value: string }>) => (this.fAt = e.detail.value)}
                ></bn-input>
              </bn-field>
            </div>`
          : this.fKind === "cron"
            ? html`<bn-field label="Cron">
                <bn-input
                  mono
                  .value=${this.fCron}
                  @input=${(e: CustomEvent<{ value: string }>) => (this.fCron = e.detail.value)}
                ></bn-input>
              </bn-field>`
            : nothing}
        <div class="row">
          <bn-field label="Time zone">
            <bn-input
              mono
              .value=${this.fZone}
              @input=${(e: CustomEvent<{ value: string }>) => (this.fZone = e.detail.value)}
            ></bn-input>
          </bn-field>
          <bn-field label="Calendar">
            <select
              .value=${this.fCalendar}
              @change=${(e: Event) => (this.fCalendar = (e.target as HTMLSelectElement).value)}
            >
              <option value="" ?selected=${this.fCalendar === ""}>Monday to Friday</option>
              ${this.calendars.map(
                (c) =>
                  html`<option value=${c.id} ?selected=${c.id === this.fCalendar}>
                    ${c.name}
                  </option>`,
              )}
            </select>
          </bn-field>
          <bn-field label="Window (workdays)">
            <bn-input
              type="number"
              .value=${this.fWindow}
              @input=${(e: CustomEvent<{ value: string }>) => (this.fWindow = e.detail.value)}
            ></bn-input>
          </bn-field>
        </div>
        <p class="hint">The new schedule starts now. Editions the old schedule missed are not made later.</p>
        <bn-button slot="footer" variant="ghost" @click=${close}>Cancel</bn-button>
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${loading}
          @click=${() => this.saveSettings()}
          >Save</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.modal === "enable" || this.modal === "channels"}
        heading=${this.modal === "enable" ? "Enable deployment" : "Change channels"}
        @close=${close}
      >
        ${err}
        ${CHANNELS.map(
          (c) =>
            html`<bn-checkbox
              label=${c.label}
              hint=${c.hint}
              ?checked=${this.fChannels.includes(c.value)}
              @change=${(e: CustomEvent<{ checked: boolean }>) =>
                this.toggleChannel(c.value, e.detail.checked)}
            ></bn-checkbox>`,
        )}
        <p class="hint">
          Mail needs an audience and report mail on this server. A webhook needs a URL. Both need a
          period rule.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${close}>Cancel</bn-button>
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${loading}
          @click=${() => this.saveChannels()}
          >${this.modal === "enable" ? "Enable" : "Save"}</bn-button
        >
      </bn-modal>

      <bn-modal ?open=${this.modal === "audience"} heading="Mail audience" @close=${close}>
        ${err}
        <bn-field label="Addresses" hint="One address per line. At most 50.">
          <textarea
            .value=${this.fAudience}
            spellcheck="false"
            @input=${(e: Event) => (this.fAudience = (e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </bn-field>
        <bn-field label="Link lifetime (days)" hint="1 to 90.">
          <bn-input
            type="number"
            .value=${this.fTtl}
            @input=${(e: CustomEvent<{ value: string }>) => (this.fTtl = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-button slot="footer" variant="ghost" @click=${close}>Cancel</bn-button>
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${loading}
          @click=${() => this.saveAudience()}
          >Save</bn-button
        >
      </bn-modal>

      <bn-modal ?open=${this.modal === "webhook"} heading="Webhook URL" @close=${close}>
        ${err}
        <bn-field label="URL" hint="HTTPS only. The host must resolve to public addresses.">
          <bn-input
            mono
            .value=${this.fURL}
            placeholder="https://"
            @input=${(e: CustomEvent<{ value: string }>) => (this.fURL = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-button slot="footer" variant="ghost" @click=${close}>Cancel</bn-button>
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${loading}
          @click=${() => this.saveWebhook()}
          >Save</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.modal === "secret"}
        heading="Webhook signing secret"
        @close=${() => {
          this.secret = "";
          close();
        }}
      >
        <p>Copy the secret now. It is never shown again.</p>
        <span class="code">${this.secret}</span>
        <bn-button slot="footer" variant="secondary" @click=${() => this.copy(this.secret)}
          >Copy</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          @click=${() => {
            this.secret = "";
            close();
          }}
          >Done</bn-button
        >
      </bn-modal>

      <bn-modal ?open=${this.modal === "send"} heading="Send an edition now" @close=${close}>
        ${err}
        <bn-field
          label="Period"
          hint="YYYY-MM, YYYY-Qn or YYYY, to match the period rule. Leave empty for the current period."
        >
          <bn-input
            mono
            .value=${this.fPeriod}
            @input=${(e: CustomEvent<{ value: string }>) => (this.fPeriod = e.detail.value)}
          ></bn-input>
        </bn-field>
        <p class="hint">
          Every month of the period must be sealed. The edition goes to every channel that is on.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${close}>Cancel</bn-button>
        <bn-button slot="footer" variant="primary" ?loading=${loading} @click=${() => this.send()}
          >Send</bn-button
        >
      </bn-modal>

      ${this.renderConfirm(
        "delete",
        "Delete deployment?",
        "The deployment, its editions and their files are removed. Its URL and every link stop working.",
        "Delete",
      )}
      ${this.renderConfirm(
        "link-key",
        "Rotate the viewer-link key?",
        "Every viewer link handed out so far stops working at once.",
        "Rotate",
      )}
      ${this.renderConfirm(
        "edition-key",
        "Rotate the edition key?",
        "The link in every report mail sent so far stops working at once.",
        "Rotate",
      )}
      ${this.renderConfirm(
        "rotate-secret",
        "Rotate the webhook secret?",
        "For 24 hours, events also carry a signature with the old secret.",
        "Rotate",
      )}
      ${this.renderConfirm(
        "remove-webhook",
        "Remove the webhook?",
        "Events are no longer posted, and the attempt history is deleted.",
        "Remove",
      )}
    `;
  }

  private renderConfirm(kind: string, heading: string, text: string, action: string) {
    return html`<bn-modal
      ?open=${this.modal === kind}
      heading=${heading}
      @close=${() => (this.modal = "")}
    >
      <p>${text}</p>
      <bn-button slot="footer" variant="ghost" @click=${() => (this.modal = "")}>Cancel</bn-button>
      <bn-button slot="footer" variant="danger" @click=${() => this.confirm(kind)}
        >${action}</bn-button
      >
    </bn-modal>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-deployment": PageDeployment;
  }
}
